import React, { useState } from "react";
import { login, register } from "../api/authApi";

export default function Login({ onAuthenticated }) {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const user =
        mode === "login"
          ? await login(username, password)
          : await register(username, password);

      onAuthenticated(user);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setUsername("");
    setPassword("");
  };

  return (
    <div className="min-h-screen w-full bg-white flex overflow-hidden">

      {/* =========================================================
          LEFT LOGIN SECTION
      ========================================================= */}
      <section className="relative w-full lg:w-[58%] min-h-screen bg-white flex items-center justify-center px-6 py-10 sm:px-10">

        {/* Subtle background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-50/60 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-50/70 blur-3xl" />

          {/* Medical grid */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.025]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="medical-grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M30 0V60M0 30H60"
                  stroke="#0f766e"
                  strokeWidth="0.6"
                />
                <circle
                  cx="30"
                  cy="30"
                  r="2"
                  fill="#0f766e"
                />
              </pattern>
            </defs>

            <rect
              width="100%"
              height="100%"
              fill="url(#medical-grid)"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[550px]">

          {/* =====================================================
              BRAND
          ===================================================== */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-3">

              {/* Logo */}
              <div className="relative w-[74px] h-[74px] flex-shrink-0">

                {/* Outer circle */}
                <div className="absolute inset-0 rounded-full border-[7px] border-[#188c94]" />

                {/* Blue arc */}
                <div
                  className="absolute inset-0 rounded-full border-[7px] border-transparent border-t-[#2568a8] border-l-[#2568a8]"
                  style={{
                    transform: "rotate(-35deg)",
                  }}
                />

                {/* Medical cross */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[38px] h-[38px]">

                    <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[13px] h-full bg-[#1c9b91]" />

                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[13px] bg-[#1c9b91]" />

                    {/* ECG line */}
                    <svg
                      className="absolute left-[2px] top-[7px] w-[34px] h-[24px]"
                      viewBox="0 0 40 24"
                      fill="none"
                    >
                      <path
                        d="M1 13H8L11 5L15 20L19 9L22 13H28L31 13L34 4L38 13"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Brand text */}
              <div>
                <div className="flex items-center">
                  <h1 className="text-[42px] sm:text-[46px] leading-none font-bold tracking-[-2px] text-[#1e5f96]">
                    Rx
                  </h1>

                  <h1 className="text-[42px] sm:text-[46px] leading-none font-bold tracking-[-2px] text-[#2878b4]">
                    DICOM
                  </h1>
                </div>

                <p className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.17em] text-gray-500 font-medium">
                  DIGITAL RADIOLOGY IMAGING SOLUTIONS
                </p>
              </div>
            </div>
          </div>

          {/* =====================================================
              LOGIN CARD
          ===================================================== */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_12px_45px_rgba(30,70,100,0.13)] px-7 py-8 sm:px-10 sm:py-9">

            {/* Heading */}
            <div className="text-center mb-7">
              <h2 className="text-[25px] sm:text-[28px] font-semibold text-[#146da5]">
                {mode === "login"
                  ? "Sign In to Your Account"
                  : "Create Your Account"}
              </h2>

              {mode === "register" && (
                <p className="mt-1.5 text-sm text-gray-500">
                  Start managing your medical imaging data
                </p>
              )}
            </div>

            {/* ===================================================
                FORM
            =================================================== */}
            <form onSubmit={submit} className="space-y-5">

              {/* Username */}
              <div>
                <div className="relative">

                  {/* User Icon */}
                  <svg
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <circle cx="12" cy="7" r="4" />
                    <path
                      d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"
                      strokeLinecap="round"
                    />
                  </svg>

                  <input
                    autoFocus
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username or Email"
                    required
                    autoComplete="username"
                    className="
                      w-full
                      h-[58px]
                      pl-14
                      pr-4
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      text-[17px]
                      text-gray-700
                      placeholder-gray-500
                      outline-none
                      transition-all
                      duration-200
                      focus:border-[#2675ad]
                      focus:ring-2
                      focus:ring-[#2675ad]/15
                    "
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="relative">

                  {/* Lock Icon */}
                  <svg
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <rect
                      x="4"
                      y="10"
                      width="16"
                      height="11"
                      rx="2"
                    />

                    <path
                      d="M8 10V7a4 4 0 0 1 8 0v3"
                      strokeLinecap="round"
                    />

                    <circle
                      cx="12"
                      cy="15.5"
                      r="1"
                      fill="currentColor"
                    />
                  </svg>

                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                    minLength={mode === "register" ? 6 : undefined}
                    autoComplete={
                      mode === "login"
                        ? "current-password"
                        : "new-password"
                    }
                    className="
                      w-full
                      h-[58px]
                      pl-14
                      pr-14
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      text-[17px]
                      text-gray-700
                      placeholder-gray-500
                      outline-none
                      transition-all
                      duration-200
                      focus:border-[#2675ad]
                      focus:ring-2
                      focus:ring-[#2675ad]/15
                    "
                  />

                  {/* Show / Hide */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <path
                          d="M3 3l18 18"
                          strokeLinecap="round"
                        />
                        <path
                          d="M10.6 10.6a2 2 0 0 0 2.8 2.8"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9.9 4.2A10.5 10.5 0 0 1 12 4c5 0 8.8 4.1 10 8-0.4 1.4-1.2 2.7-2.2 3.8"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6.1 6.1C3.9 7.8 2.5 10 2 12c1.2 3.9 5 8 10 8 1.7 0 3.2-.4 4.6-1.1"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <path
                          d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                {mode === "register" && (
                  <p className="mt-2 text-xs text-gray-400">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>

              {/* Forgot password / Request account */}
              {mode === "login" && (
                <div className="flex items-center justify-between">

                  {/* Remember me */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) =>
                        setRememberMe(e.target.checked)
                      }
                      className="
                        w-4
                        h-4
                        rounded
                        border-gray-300
                        text-[#1778a7]
                        focus:ring-[#1778a7]
                      "
                    />

                    <span className="text-sm text-gray-500">
                      Remember me
                    </span>
                  </label>

                  {/* Forgot */}
                  <button
                    type="button"
                    className="
                      text-sm
                      text-[#176aa0]
                      hover:text-[#0f527e]
                      font-medium
                      transition-colors
                    "
                    onClick={() => {
                      // Add your forgot password logic here
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <div className="flex items-start gap-2">

                    <svg
                      className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z" />
                    </svg>

                    <p className="text-sm text-red-600">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* =================================================
                  SIGN IN BUTTON
              ================================================= */}
              <button
                type="submit"
                disabled={loading}
                className="
                  w-full
                  h-[58px]
                  rounded-lg
                  bg-gradient-to-r
                  from-[#12ad95]
                  to-[#08a88d]
                  hover:from-[#0e9d87]
                  hover:to-[#07977f]
                  active:scale-[0.99]
                  text-white
                  text-[18px]
                  font-semibold
                  shadow-[0_7px_18px_rgba(10,170,145,0.25)]
                  transition-all
                  duration-200
                  disabled:opacity-60
                  disabled:cursor-not-allowed
                "
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">

                    <svg
                      className="animate-spin w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="opacity-30"
                      />

                      <path
                        d="M21 12a9 9 0 0 0-9-9"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>

                    Signing In...
                  </span>
                ) : mode === "login" ? (
                  "Sign In"
                ) : (
                  "Create Account"
                )}
              </button>

              {/* =================================================
                  ACCOUNT REQUEST
              ================================================= */}
              <div className="text-center pt-1">

                {mode === "login" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => switchMode("register")}
                      className="
                        text-[16px]
                        font-medium
                        text-[#1c6ea3]
                        hover:text-[#10547e]
                        transition-colors
                      "
                    >
                      Request an Account
                    </button>

                    <p className="mt-5 text-xs text-gray-500">
                      By signing in, you agree to our{" "}
                      <button
                        type="button"
                        className="text-[#176aa0] hover:underline"
                      >
                        Terms of Service
                      </button>
                      .
                    </p>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="
                      text-sm
                      font-medium
                      text-[#176aa0]
                      hover:underline
                    "
                  >
                    ← Back to Sign In
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* =====================================================
              FOOTER
          ===================================================== */}
          <div className="text-center mt-7">

            <p className="text-[16px] text-gray-500">
              Streamlining Radiology with Precision.
            </p>

            <div className="flex items-center justify-center gap-3 mt-5">
              <div className="h-px w-8 bg-gray-200" />

              <p className="text-[11px] text-gray-400">
                Developed by{" "}
                <span className="font-semibold text-[#1673a7]">
                  S3D Technology Pvt Ltd.
                </span>
              </p>

              <div className="h-px w-8 bg-gray-200" />
            </div>

            <div className="flex items-center justify-center gap-2 mt-4 text-[10px] text-gray-300">
              <span>Rx DICOM v2.0</span>
              <span>•</span>
              <span>HIPAA Compliant</span>
              <span>•</span>
              <span>Secure Connection</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===========================================================
          RIGHT IMAGE / BRANDING SECTION
      =========================================================== */}
      <section className="hidden lg:flex relative w-[42%] min-h-screen overflow-hidden bg-[#eaf3f7]">

        {/* Background medical image placeholder.
            Replace /assets/radiology-bg.jpg with your actual image. */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/radiology-bg.png')",
          }}
        />

        {/* White overlay */}
        <div className="absolute inset-0 bg-white/65 backdrop-blur-[1px]" />

        {/* Blue tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-blue-50/30 to-cyan-100/40" />

        {/* =========================================================
            LARGE RX WATERMARK
        ========================================================= */}
        <div className="absolute right-[-120px] top-[-60px] opacity-[0.20]">

          <svg
            width="650"
            height="900"
            viewBox="0 0 650 900"
            fill="none"
          >
            {/* Large circular arc */}
            <path
              d="M500 40C330 40 190 170 190 330C190 490 330 620 500 620"
              stroke="#5d6269"
              strokeWidth="72"
            />

            {/* R shape */}
            <path
              d="M300 205V610M300 205H430C500 205 535 245 535 300C535 355 500 390 430 390H300M430 390L540 610"
              stroke="#686d73"
              strokeWidth="65"
              strokeLinecap="square"
            />

            {/* Medical ECG */}
            <path
              d="M400 450H440L465 370L500 535L535 430L555 450H620"
              stroke="#326a92"
              strokeWidth="18"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Cross */}
            <path
              d="M575 100V300M475 200H675"
              stroke="#6684a2"
              strokeWidth="55"
            />
          </svg>
        </div>

        {/* =========================================================
            RIGHT CONTENT
        ========================================================= */}
        <div className="relative z-10 w-full flex items-center justify-center p-12">

          <div className="max-w-[480px] mt-[-30px]">

            {/* Decorative medical icon */}
            <div className="mb-7">

              <div className="w-[76px] h-[76px] rounded-full border border-white/70 bg-white/50 backdrop-blur-md flex items-center justify-center shadow-lg">

                <svg
                  className="w-12 h-12 text-[#1b8290]"
                  viewBox="0 0 48 48"
                  fill="none"
                >
                  <path
                    d="M3 24h9l4-11 7 23 5-16 4 4h13"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <circle
                    cx="24"
                    cy="24"
                    r="21"
                    stroke="currentColor"
                    strokeWidth="1"
                    opacity=".25"
                  />
                </svg>
              </div>
            </div>

            {/* Main text */}
            <h2 className="text-[38px] xl:text-[44px] leading-[1.1] font-semibold text-gray-600 tracking-tight">
              Streamlining
              <br />
              Radiology
              <br />
              <span className="text-gray-500">
                with Precision.
              </span>
            </h2>

            <p className="mt-7 max-w-[390px] text-[16px] leading-7 text-gray-500">
              Advanced digital radiology imaging solutions
              designed to simplify your workflow and deliver
              precision at every step.
            </p>

            {/* Feature line */}
            <div className="mt-9 flex items-center gap-4">

              <div className="h-[2px] w-14 bg-[#1a9b91]" />

              <span className="text-xs uppercase tracking-[0.2em] font-medium text-gray-400">
                Digital Imaging
              </span>
            </div>

            {/* Bottom medical card */}
            <div className="mt-12 flex items-center gap-4">

              <div className="w-14 h-14 rounded-xl bg-white/60 backdrop-blur-md shadow-md flex items-center justify-center border border-white">

                <svg
                  className="w-8 h-8 text-[#197c91]"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M16 4L26 8v7c0 7-4.4 10.9-10 13-5.6-2.1-10-6-10-13V8l10-4Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />

                  <path
                    d="M16 10v10M11 15h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-600">
                  Secure Medical Imaging
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  Reliable • Secure • Precise
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/40 to-transparent pointer-events-none" />
      </section>

      {/* ===========================================================
          MOBILE BRANDING
      =========================================================== */}
      <div className="lg:hidden fixed bottom-3 left-0 right-0 text-center pointer-events-none">
        <p className="text-[9px] text-gray-300">
          Rx DICOM • Digital Radiology Imaging Solutions
        </p>
      </div>
    </div>
  );
}